/**
 * Build-menu icons, rendered from each entity's **own** mesh (R24, U16).
 *
 * An icon is never a separate asset, so it can never drift from the unit it
 * depicts — and there is no image file to commit. Each entity's geometry is
 * drawn once at boot through a small orthographic camera into a render target,
 * read back, and painted into a canvas the sidebar owns.
 */

import {
  AmbientLight,
  DirectionalLight,
  Mesh,
  MeshLambertMaterial,
  NearestFilter,
  OrthographicCamera,
  Scene,
  Vector3,
  type BufferGeometry,
  type WebGLRenderer,
  WebGLRenderTarget,
} from 'three'
import type { Palette } from '../forge/palette.js'
import { defById, type EntityDef } from '../sim/data/entities.js'
import { bakeShades } from '../render/entities.js'
import { detailSurfaces } from '../render/surfaces.js'

/**
 * Pixel size of a rendered icon.
 *
 * A sidebar tile is around ninety-six display pixels wide, so rendering at 44
 * and scaling up was a better-than-two-times magnification of an already small
 * render — which is exactly what made the icons look pixelated next to a world
 * that is drawn at native resolution.
 */
export const ICON_SIZE = 128

export interface IconSource {
  /** Geometry for an entity kind. Supplied by the renderer, later by the forge. */
  geometryFor(kind: number): BufferGeometry
}

/**
 * Renders and caches one icon per entity kind.
 *
 * Reuses the main renderer rather than creating a second WebGL context —
 * browsers cap contexts, and a second one for icons would be spent badly.
 */
export class IconForge {
  private readonly scene = new Scene()
  private readonly camera: OrthographicCamera
  private readonly target: WebGLRenderTarget
  private readonly cache = new Map<number, HTMLCanvasElement>()
  private readonly pixels: Uint8Array
  private mesh: Mesh | null = null

  constructor(
    private readonly renderer: WebGLRenderer,
    private readonly palette: Palette,
    private readonly source: IconSource,
  ) {
    // The same locked three-quarter view the world uses, so an icon reads as
    // the thing the player will see on the battlefield.
    this.camera = new OrthographicCamera(-1.4, 1.4, 1.4, -1.4, 0.01, 40)
    this.camera.position.set(2.2, 2.6, 2.2)
    this.camera.lookAt(new Vector3(0, 0.3, 0))

    this.target = new WebGLRenderTarget(ICON_SIZE, ICON_SIZE, {
      minFilter: NearestFilter,
      magFilter: NearestFilter,
    })
    this.pixels = new Uint8Array(ICON_SIZE * ICON_SIZE * 4)

    const key = new DirectionalLight(0xffffff, 2.67)
    key.position.set(0.5, 0.8, 0.35).normalize()
    this.scene.add(key)
    this.scene.add(new AmbientLight(0xffffff, 1.4))
  }

  /** A canvas showing this entity kind. Rendered once, then cached. */
  iconFor(kind: number, ownerSlot: number): HTMLCanvasElement {
    const cached = this.cache.get(kind * 16 + ownerSlot)
    if (cached) return cached

    const def = defById(kind)
    const canvas = document.createElement('canvas')
    canvas.width = ICON_SIZE
    canvas.height = ICON_SIZE
    if (def) this.render(def, ownerSlot, canvas)
    this.cache.set(kind * 16 + ownerSlot, canvas)
    return canvas
  }

  private render(def: EntityDef, ownerSlot: number, canvas: HTMLCanvasElement): void {
    const geometry = this.source.geometryFor(def.id)
    // The identical shade mapping the world uses, so an icon can never drift
    // from the unit it depicts.
    const baked = bakeShades(geometry, this.palette, ownerSlot)
    const material = new MeshLambertMaterial({ vertexColors: true, flatShading: true })
    detailSurfaces(material)

    if (this.mesh) this.scene.remove(this.mesh)
    this.mesh = new Mesh(baked, material)
    // Scale so a 3×3 structure and a 1×1 unit both fill the frame.
    const span = Math.max(def.width, def.height, 1)
    this.mesh.scale.setScalar(1.6 / span)
    this.mesh.position.set(0, 0, 0)
    this.scene.add(this.mesh)

    const previous = this.renderer.getRenderTarget()
    this.renderer.setRenderTarget(this.target)
    this.renderer.setClearColor(0x241811, 1)
    this.renderer.clear()
    this.renderer.render(this.scene, this.camera)
    this.renderer.readRenderTargetPixels(this.target, 0, 0, ICON_SIZE, ICON_SIZE, this.pixels)
    this.renderer.setRenderTarget(previous)
    this.renderer.setClearColor(0x0a0705, 1)

    const ctx = canvas.getContext('2d')
    if (ctx) {
      const image = ctx.createImageData(ICON_SIZE, ICON_SIZE)
      // WebGL reads bottom-up; the canvas is top-down.
      for (let y = 0; y < ICON_SIZE; y++) {
        const src = (ICON_SIZE - 1 - y) * ICON_SIZE * 4
        const dst = y * ICON_SIZE * 4
        image.data.set(this.pixels.subarray(src, src + ICON_SIZE * 4), dst)
      }
      ctx.putImageData(image, 0, 0)
    }
    material.dispose()
    baked.dispose()
  }

  dispose(): void {
    if (this.mesh) this.scene.remove(this.mesh)
    this.target.dispose()
    this.cache.clear()
  }
}
